/**
 * Fusion d'un lot d'enregistrements distants (entrees ou dossiers, chiffres)
 * dans une collection locale. Pure fonction de metadonnees claires (voir
 * `conflict.ts`) : aucun dechiffrement, donc utilisable directement sur ce
 * que le serveur renvoie (ciphertext-only, `docs/ARCHITECTURE.md` §4).
 *
 * Idempotent : fusionner deux fois le meme lot distant produit le meme
 * resultat (propriete testee).
 */
import { isNewer, resolveConflict, type ConflictRecord } from './conflict.js';

export interface SyncConflict<T extends ConflictRecord> {
  readonly id: string;
  readonly local: T;
  readonly remote: T;
  readonly winner: T;
}

export interface MergeResult<T extends ConflictRecord> {
  /** Etat fusionne, un enregistrement par `id`. */
  readonly merged: readonly T[];
  /** Paires ou local et distant divergeaient (utile pour un journal/audit). */
  readonly conflicts: readonly SyncConflict<T>[];
}

/**
 * Fusionne `remote` (lot recu du serveur) dans `local` (etat de l'appareil).
 * Les enregistrements absents d'un cote sont conserves tels quels ; ceux
 * presents des deux cotes sont tranches par `resolveConflict`.
 */
export function mergeVaultRecords<T extends ConflictRecord>(
  local: readonly T[],
  remote: readonly T[],
): MergeResult<T> {
  const byId = new Map<string, T>();
  for (const record of local) byId.set(record.id, record);

  const conflicts: SyncConflict<T>[] = [];
  for (const record of remote) {
    const existing = byId.get(record.id);
    if (existing === undefined) {
      byId.set(record.id, record);
      continue;
    }
    if (existing.revision === record.revision && existing.updatedAt === record.updatedAt) {
      continue;
    }
    const winner = resolveConflict(existing, record);
    conflicts.push({ id: record.id, local: existing, remote: record, winner });
    byId.set(record.id, winner);
  }

  return { merged: Array.from(byId.values()), conflicts };
}

/**
 * Parmi une collection fusionnee, ceux encore actifs (non supprimes) — utile
 * pour l'affichage, une fois les tombstones exploites pour la synchro.
 */
export function withoutTombstones<T extends ConflictRecord & { readonly deletedAt: number | null }>(
  records: readonly T[],
): T[] {
  return records.filter((record) => record.deletedAt === null);
}

/**
 * Purge definitive des tombstones supprimes strictement avant `cutoff` (epoch
 * ms) -- politique de retention (`docs/DATA_PROTECTION.md` §3). Les
 * enregistrements actifs et les suppressions plus recentes sont conserves.
 */
export function purgeExpiredTombstones<T extends { readonly deletedAt: number | null }>(
  records: readonly T[],
  cutoff: number,
): T[] {
  return records.filter((record) => record.deletedAt === null || record.deletedAt >= cutoff);
}

/** Ce que le local doit renvoyer au serveur : tout ce qui a change depuis `since`. */
export function changedSince<T extends ConflictRecord & { readonly updatedAt: number }>(
  records: readonly T[],
  since: number,
): T[] {
  return records.filter((record) => record.updatedAt > since);
}

export interface TransferPlan<L extends ConflictRecord, R extends ConflictRecord> {
  /** Versions locales absentes du distant ou plus recentes : a envoyer. */
  readonly toPush: L[];
  /** Versions distantes plus recentes que le local (ou inconnues et actives) : a telecharger. */
  readonly toFetch: R[];
}

/**
 * Plan de transfert d'enregistrements VOLUMINEUX (pieces jointes) : compare
 * l'etat local aux metadonnees distantes (sans contenu) pour n'echanger que
 * ce qui differe, au lieu de renvoyer tout le coffre a chaque synchro. Un
 * tombstone inconnu de l'autre cote n'est ni envoye ni telecharge (rien a effacer).
 * Ce qui est telecharge doit ensuite etre authentifie puis fusionne
 * (`mergeVaultRecords`) : ce plan ne fait confiance a aucune metadonnee.
 */
export function planRecordTransfer<
  L extends ConflictRecord & { readonly deletedAt: number | null },
  R extends ConflictRecord & { readonly deletedAt: number | null },
>(local: readonly L[], remote: readonly R[]): TransferPlan<L, R> {
  const remoteById = new Map(remote.map((record) => [record.id, record]));
  const localById = new Map(local.map((record) => [record.id, record]));
  return {
    toPush: local.filter((record) => {
      const other = remoteById.get(record.id);
      return other === undefined ? record.deletedAt === null : isNewer(record, other);
    }),
    toFetch: remote.filter((record) => {
      const other = localById.get(record.id);
      return other === undefined ? record.deletedAt === null : isNewer(record, other);
    }),
  };
}

export { isNewer, resolveConflict };
export type { ConflictRecord };
